import authSession from "../lib/authSession";
// components/PaystackPaymentButton.js
import React from "react";
import {PaystackButton} from "react-paystack";
import axios from "axios";
import {API_URL} from "../constants";

// Normalize an API payload into a string so we never render an object
// (which would trigger "Objects are not valid as a React child").
const toDisplayString = (value, fallback) => {
  if (value === undefined || value === null) return fallback ?? "";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  if (typeof value === "object") {
    const candidate = value.message ?? value.error ?? value.description;
    if (candidate !== undefined && candidate !== null) {
      return toDisplayString(candidate, fallback);
    }
    return fallback ?? JSON.stringify(value);
  }
  return fallback ?? "";
};

// Configure axios base URL if not already done globally
axios.defaults.baseURL = `${API_URL}/api`; // Your backend URL

const PaystackPaymentButton = ({
  itemId,
  userId,
  itemType,
  amount,
  email,
  onPaymentSuccess,
  onPaymentClose,
  buttonText = "Enroll Now",
  disabled = false,
  showToast
}) => {
  const config = {
    reference: new Date().getTime().toString(), // Will be overwritten by backend's reference
    email: email,
    amount: amount * 100, // Paystack expects amount in kobo
    publicKey: process.env.REACT_APP_PAYSTACK_PUBLIC_KEY || "pk_test_YOUR_PAYSTACK_PUBLIC_KEY" // Use env variable
  };
  const [isLoading, setIsLoading] = React.useState(false);

  const handlePaystackSuccessAction = async (response) => {
    console.log("Paystack Success Response:", response);
    // After Paystack successful popup, you should still verify on your backend
    // because the user could close the popup before the redirect.
    // The backend's verify endpoint is primarily for handling Paystack's callback.
    // However, the redirect from Paystack to `/payment-success?reference=...` is crucial.
    // For now, this component will just trigger `onPaymentSuccess` and the redirect will handle the rest.
    showToast("Payment initiated successfully! Redirecting for verification...", true);
    onPaymentSuccess && onPaymentSuccess(response);
  };

  const handlePaystackCloseAction = () => {
    console.log("Paystack closed.");
    showToast("Payment window closed.", true);
    onPaymentClose && onPaymentClose();
  };

  const componentProps = {
    ...config,
    text: buttonText,
    onSuccess: handlePaystackSuccessAction,
    onClose: handlePaystackCloseAction,
    disabled: disabled,
    // style example, adjust as needed with Bootstrap classes
    className: `btn btn-primary ${disabled ? "disabled" : ""}`
  };

  // The actual initialization happens on the backend
  const handleInitiatePayment = async () => {
    try {
      setIsLoading(true);
      const token = authSession.getToken();
      if (!token) {
        showToast("Please log in to make a purchase.", false);
        return;
      }

      const returnUrl =
        itemType === "course"
          ? `${window.location.origin}/content-library/course/${itemId}/read`
          : `${window.location.href}`;

      const res = await axios.post(
        "/payment/initialize",
        {itemId, itemType, email, userId, returnUrl},
        {
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "application/json"
          }
        }
      );

      const {authorizationUrl, reference} = res.data;

      // Update the config with the backend-generated reference
      componentProps.reference = reference;

      // Redirect to Paystack's authorization URL
      window.location.href = authorizationUrl;
    } catch (err) {
      console.error("Error initiating payment:", err.response ? err.response.data : err);
      showToast(toDisplayString(err.response?.data?.message, "Failed to initiate payment."), false);
    } finally {
      setIsLoading(false);
    }
  };

  // Since we are redirecting to Paystack's authorization_url,
  // the react-paystack button's `onSuccess` and `onClose` are not directly used for the final verification.
  // Instead, the `handleInitiatePayment` function will trigger the redirect.
  // We'll use a standard button here and handle the redirect manually.
  return (
    <button
      onClick={handleInitiatePayment}
      className={componentProps.className}
      disabled={componentProps.disabled}
    >
      {isLoading ? (
        <div className="loader mb-0"></div>
      ) : (
        `${buttonText} - ${new Intl.NumberFormat("en-NG", {
          style: "currency",
          currency: "NGN",
          minimumFractionDigits: 2
        }).format(amount)}`
      )}
    </button>
  );
};

export default PaystackPaymentButton;
